import { matchPath } from "react-router-dom";

/* Maps a workspace-relative location to the analytics `page`/`scope`/`agentId`
 * context (Notion "📊 Tokenwise" §PageEntered). The router mounts everything
 * under /w/:workspaceId; pages live under `/all/*` (workspace scope)
 * or `/agents/:agentId/*` (agent scope) — see router/Router.tsx. We match on the
 * path *after* the /w/:workspaceId prefix so page detection is independent of
 * the workspace id and the app basename. */

export type PageScope = "workspace" | "agent";

export interface PageInfo {
  /** Page name from the spec's enum, or undefined for unmatched routes. */
  page?: string;
  /** "workspace" for /all views, "agent" for /agents/:agentId views. */
  scope?: PageScope;
  /** The agent in scope, when scope === "agent". */
  agentId?: string;
}

/** The router basename (App.tsx) — the app is served at root, so this is empty
 *  and stripping is a no-op. Kept as a single source in case a base path
 *  returns. */
const BASENAME = "";

/* Gated/public routes that live OUTSIDE /w/:workspaceId (auth screens, the
 * shareable docs link, the invite-accept page) — see router/Router.tsx. They
 * carry no workspace scope, so they're matched on the basename-stripped path
 * before the workspace-relative logic. */
const NON_WORKSPACE_ROUTES: { pattern: string; page: string }[] = [
  { pattern: "/auth", page: "login" },
  { pattern: "/login", page: "login" },
  { pattern: "/signup", page: "signup" },
  { pattern: "/check-email", page: "checkEmail" },
  { pattern: "/forgot-password", page: "forgotPassword" },
  { pattern: "/welcome", page: "welcome" },
  { pattern: "/invite/:invitationId", page: "acceptInvitation" },
];

/** Strip just the basename, leaving the router-relative path. */
function stripBasename(pathname: string): string {
  return pathname.startsWith(BASENAME)
    ? pathname.slice(BASENAME.length) || "/"
    : pathname;
}

/** Strip the basename and the /w/:workspaceId prefix, returning the
 *  workspace-relative path (leading "/", no trailing slash). Returns "" when
 *  the path isn't under a workspace (e.g. /login). */
export function workspaceRelativePath(pathname: string): string {
  let p = pathname;
  if (p.startsWith(BASENAME)) p = p.slice(BASENAME.length);

  const m = matchPath({ path: "/w/:workspaceId/*", end: false }, p);
  if (!m) return "";

  const rest = m.params["*"] ?? "";
  return rest ? `/${rest.replace(/\/+$/, "")}` : "/";
}

/* Workspace-relative route patterns → page name. Must mirror the routes
 * mounted in router/Router.tsx. Agent patterns carry the :agentId param so we
 * can surface it. */
const ROUTES: { pattern: string; page: string; scope: PageScope }[] = [
  { pattern: "/", page: "overview", scope: "workspace" },
  { pattern: "/welcome", page: "welcome", scope: "workspace" },

  // Per-agent views (agent scope).
  { pattern: "/agents/:agentId", page: "agentDetail", scope: "agent" },

  // Settings, org (workspace scope).
  { pattern: "/settings/api-keys", page: "apiKeys", scope: "workspace" },
  { pattern: "/settings/billing", page: "billing", scope: "workspace" },
  { pattern: "/org", page: "organization", scope: "workspace" },
  { pattern: "/org/members", page: "members", scope: "workspace" },
];

/** Resolve the analytics page context for a full router pathname. */
export function pageInfo(pathname: string): PageInfo {
  // Gated/public pages outside /w/:workspaceId (auth, docs link, invite).
  const abs = stripBasename(pathname);
  for (const route of NON_WORKSPACE_ROUTES) {
    if (matchPath({ path: route.pattern, end: true }, abs)) {
      return { page: route.page };
    }
  }

  const rel = workspaceRelativePath(pathname);
  if (!rel) return {};

  for (const route of ROUTES) {
    const m = matchPath({ path: route.pattern, end: true }, rel);
    if (!m) continue;

    const agentId = route.scope === "agent" ? m.params.agentId : undefined;
    return {
      page: route.page,
      scope: route.scope,
      ...(agentId ? { agentId } : {}),
    };
  }

  return {};
}
